/**
 * useSubjectFeesListScreen.js
 * ---------------------------
 * Hook for Subjectwise Fees Master list screen using shared useMasterList
 * and asynchronous JobEngine export.
 */
import { useState, useEffect, useMemo } from 'react';
import { useMasterList } from '../../common/useMasterList';
import * as subjectFeesApi from '../api/subjectFeesApi';
import { getCourseOptions } from '../../courses/api/coursesApi';
import { getSubjectOptions } from '../../subjects/api/subjectsApi';
import { useExportJob } from '../../../../hooks/useExportJob';

export function useSubjectFeesListScreen() {
  const [aYear, setAYear] = useState('');
  const [courseId, setCourseId] = useState('');
  const [subjectId, setSubjectId] = useState('');

  const [courseOptions, setCourseOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadDropdowns() {
      try {
        const [cOpts, sOpts] = await Promise.all([
          getCourseOptions().catch(() => []),
          getSubjectOptions().catch(() => []),
        ]);
        if (isMounted) {
          setCourseOptions(cOpts || []);
          setSubjectOptions(sOpts || []);
        }
      } catch (err) {
        console.error('Failed to load filter options for Subjectwise Fees:', err);
      }
    }
    loadDropdowns();
    return () => {
      isMounted = false;
    };
  }, []);

  const extraParams = useMemo(() => {
    const params = {};
    if (aYear && aYear.trim()) params.aYear = aYear.trim();
    if (courseId) params.courseId = Number(courseId);
    if (subjectId) params.subjectId = Number(subjectId);
    return params;
  }, [aYear, courseId, subjectId]);

  const master = useMasterList({
    entityName: 'Subjectwise Fee',
    apiClient: subjectFeesApi.subjectFeesClient,
    fetchList: subjectFeesApi.listSubjectFees,
    archiveItem: subjectFeesApi.archiveSubjectFees,
    deleteItem: subjectFeesApi.deleteSubjectFees,
    extraParams,
  });

  const { triggerExport, isExporting } = useExportJob({
    module: 'subject_fees',
    fileFormat: 'XLSX',
  });

  const handleToolSelect = (toolKey) => {
    if (toolKey === 'download' || toolKey === 'export_excel') {
      triggerExport({
        viewMode: master.state.viewMode,
        searchText: master.state.searchText,
        aYear,
        courseId,
        subjectId,
      });
    }
  };

  const handleResetFilters = () => {
    setAYear('');
    setCourseId('');
    setSubjectId('');
    master.actions.resetFilters();
  };

  return {
    ...master,
    state: {
      ...master.state,
      aYear,
      courseId,
      subjectId,
      courseOptions,
      subjectOptions,
      isExporting,
    },
    actions: {
      ...master.actions,
      setAYear,
      setCourseId,
      setSubjectId,
      resetFilters: handleResetFilters,
      handleToolSelect,
    },
  };
}
